import { defineStore } from 'pinia';
import { type User, UserTag } from 'gpcore-grpc/gpcore/api/cloud/v1/user_pb';
import useAuthService from '@grpc/composables/useAuthService';
import { type Ref, computed, reactive, toRefs } from 'vue';
import { useRootStore } from '@pinia/index';
import * as Sentry from '@sentry/vue';
import { setInterval, setTimeout } from 'worker-timers';
import type { KeycloakTokenParsed } from 'keycloak-js';
import { create } from '@bufbuild/protobuf';
import uuid from '../../lib/uuid';
import { GetUserRequestSchema } from 'gpcore-grpc/gpcore/api/auth/v1/requests_pb';

type SerializableUser = Pick<
  User,
  'avatarUrl' | 'email' | 'fullName' | 'username' | 'id' | 'confirmed' | 'timezone' | 'tags'
>;
export const convertProtoUser = ({
  avatarUrl,
  email,
  fullName,
  username,
  id,
  confirmed,
  timezone,
  tags,
}: User): SerializableUser => ({
  avatarUrl,
  email,
  fullName,
  username,
  id,
  confirmed,
  timezone,
  tags,
});

export interface Session {
  id: string;
  user: SerializableUser;
  accessToken: string;
  parsedToken: KeycloakTokenParsed;
  impersonated?: boolean;
  refreshToken?: string;
  refreshInterval?: number;
  /**
   * Expires at in seconds. When using, always multiply by 1000 to get the correct timestamp
   */
  expiresAt?: number;
}

export interface MainSession extends Session {
  impersonated: false;
}

const SuperAdminRole = 'super-admin';

export interface SessionState {
  mainSession: MainSession;
  sessions: Session[];
  selectedSessionId: string;
  isLoggedIn: boolean;
  /**
   * To avoid the UI to flash when the session is not yet initialized, especially, when an impersonation is happening
   */
  initCompleted: boolean;
}

export const useSessionStore = defineStore(
  'session',
  () => {
    const root = useRootStore();
    console.log('sessionStore', root.keycloak.authenticated);

    const state = reactive<SessionState>({
      mainSession: null,
      sessions: [],
      isLoggedIn: root.keycloak.authenticated,
      selectedSessionId: null,
      initCompleted: false,
    });

    const roles: Ref<string[]> = computed(() => {
      if (
        root.keycloak.tokenParsed != null &&
        root.keycloak.tokenParsed.resource_access != null &&
        root.keycloak.tokenParsed.resource_access.gpcloud != null
      ) {
        return root.keycloak.tokenParsed.resource_access.gpcloud.roles;
      }
      return [];
    });

    const hasRole = (role: string) => {
      if (roles.value.includes(SuperAdminRole)) {
        return true;
      }

      return roles.value.includes(role);
    };

    const hasTag = (tag: UserTag) => {
      return user.value?.tags.includes(tag);
    };

    const session = computed(() => {
      if (
        state.selectedSessionId == null ||
        state.mainSession == null ||
        state.mainSession.id === state.selectedSessionId
      ) {
        return state.mainSession;
      }

      return state.sessions.find((s) => s.id === state.selectedSessionId);
    });

    const token = computed(() => {
      if (session.value == null) {
        return null;
      }

      return session.value.accessToken;
    });

    const user = computed(() => {
      if (session.value == null) {
        return null;
      }

      return session.value.user;
    });

    /**
     * A session is ready when we have
     * - a logged in state from keycloak
     * - a main session and its user
     * - a current session and its user (either the main session or an impersonated session)
     */
    const isSessionReady = computed(
      () =>
        state.isLoggedIn &&
        state.mainSession != null &&
        state.mainSession.user != null &&
        session.value != null &&
        user.value != null &&
        state.initCompleted,
    );

    const authService = useAuthService();
    /**
     * Loads the user with the session.accessToken and returns it
     */
    const loadUser = async (): Promise<User> => {
      const request = create(GetUserRequestSchema);

      const { response, error } = await authService.executeApiCall('getUser', request);
      if (error != null) throw error;

      return response.user;
    };

    const setMainUser = (user: User) => {
      state.mainSession.user = convertProtoUser(user);
    };

    /**
     * Load the main user and set it in the store.
     * Retries with exponential backoff to avoid hammering the API on transient failures.
     */
    const loadMainUser = async (maxRetries = 3) => {
      const previousSelectedSessionId = state.selectedSessionId;
      if (state.mainSession.user == null) {
        state.selectedSessionId = state.mainSession.id;

        let lastError: unknown;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          if (attempt > 0) {
            const delay = Math.min(1000 * 2 ** (attempt - 1), 10000);
            await new Promise((resolve) => setTimeout(resolve, delay));
          }

          try {
            const user = await loadUser();
            setMainUser(user);

            Sentry.setUser({
              id: user.id,
              username: user.username,
              email: user.email,
            });

            state.selectedSessionId = previousSelectedSessionId;
            return;
          } catch (error) {
            lastError = error;
            console.warn(
              `[Auth] loadMainUser attempt ${attempt + 1}/${maxRetries + 1} failed`,
              error,
            );
          }
        }

        state.selectedSessionId = previousSelectedSessionId;
        throw lastError;
      }
    };

    /**
     * Iniliazes a session by fetching the user and setting it in the store
     */
    const initSession = async (idToInit: string) => {
      // set the new session as the main session
      state.selectedSessionId = idToInit;

      try {
        // now fetch the user
        // load user will use the added access token to fetch the user
        const user = await loadUser();

        // update the user in the session
        state.sessions.find((s) => s.id === idToInit).user = convertProtoUser(user);
      } catch (error) {
        // remove the session from the sessions array
        state.sessions = state.sessions.filter((s) => s.id !== idToInit);
        throw error;
      }
    };

    /**
     * Adds a new session to the store based on the token
     *
     * Iniatializes the session and loads the user
     */
    const addSession = async (accessToken: string, expiresAtStr: string) => {
      const previousSelectedSessionId = state.selectedSessionId;
      const expiresAt = parseInt(expiresAtStr, 10);

      const newId = uuid();
      state.sessions.push({
        id: newId,
        user: null,
        accessToken,
        expiresAt: Number.isNaN(expiresAt) ? undefined : expiresAt,
        parsedToken: undefined,
        impersonated: true,
      });

      state.selectedSessionId = newId;

      try {
        const user = await loadUser();

        const existingSession = state.sessions.find((s) => s.user != null && s.user.id === user.id);

        // avoid adding the same user twice
        if (
          // now verify, if the user is the main session
          state.mainSession.user.id === user.id ||
          // now verify, if the user is already in the sessions array
          existingSession != null
        ) {
          // remove the session from the sessions array
          state.sessions = state.sessions
            // look from the end to the beginning
            .reverse()
            // filter out the session with the same id
            .filter((s) => s.id !== newId)
            // reverse the array back
            .reverse();

          if (existingSession) {
            // if it is an existing session, update the user, access token and expiresAt
            existingSession.user = convertProtoUser(user);
            existingSession.accessToken = accessToken;
            const expiresAt = parseInt(expiresAtStr, 10);
            existingSession.expiresAt = Number.isNaN(expiresAt) ? undefined : expiresAt;
            // also switch to the existing session
            state.selectedSessionId = existingSession.id;

            return;
          }

          // restore the previous selected session
          state.selectedSessionId = previousSelectedSessionId;

          return;
        }

        state.sessions.find((s) => s.id === newId).user = convertProtoUser(user);
      } catch (error) {
        // remove the session from the sessions array
        state.sessions = state.sessions.filter((s) => s.id !== newId);
        throw error;
      }
    };

    /**
     * Switch to a different session by changing the selectedSessionId
     *
     * @param session Session to switch to
     */
    const switchSession = (session: Session | string) => {
      if (typeof session === 'string') {
        state.selectedSessionId = session;
        return;
      }

      state.selectedSessionId = session.id;
    };

    /**
     * Impersonating is when selectedSessionId is not the same as the mainSession.id
     */
    const isImpersonating = computed(() => {
      return state.selectedSessionId !== state.mainSession.id;
    });

    const login = async (url: string) => {
      await root.keycloak.login({
        redirectUri: url,
      });
    };

    const startRefreshToken = async (onRefreshTokenError?: () => void) => {
      let id = null;

      if (state.mainSession != null && state.mainSession.id != null) {
        id = state.mainSession.id;
      } else {
        id = uuid();
      }

      state.mainSession = {
        id,
        user: null,
        accessToken: root.keycloak.token,
        parsedToken: root.keycloak.tokenParsed,
        impersonated: false,
      };

      if (state.selectedSessionId == null) {
        state.selectedSessionId = id;
      }

      if (state.mainSession.refreshInterval == null) {
        // Maybe think about replacing this with a worker interval because
        // it can make troubles when the tab is in the background.

        state.mainSession.refreshInterval = setInterval(() => {
          root.keycloak
            .updateToken(30)
            .then((refreshed: boolean) => {
              if (refreshed) {
                state.mainSession.accessToken = root.keycloak.token;
                console.debug('[Auth] Token was successfully refreshed');
              } else {
                console.debug('[Auth] Token is still valid');
              }
            })
            .catch((e) => {
              Sentry.captureException(e);
              console.error('[Auth] Failed to refresh token', e);

              if (onRefreshTokenError) {
                onRefreshTokenError();
              }

              setTimeout(() => {
                window.location.reload();
              }, 5000);
            });
        }, 1000 * 10);
      }
    };

    const logout = () => {
      localStorage.removeItem('gportal-cloud-project');
      localStorage.removeItem('session');
      root.keycloak.logout();
      // a reload is not necessary, because the keycloak logout will automatically redirect to the login page
    };

    return {
      ...toRefs(state),
      startRefreshToken,
      logout,
      login,
      loadUser,
      loadMainUser,
      setMainUser,
      addSession,
      initSession,
      switchSession,
      hasRole,
      hasTag,
      roles,
      user,
      token,
      session,
      isImpersonating,
      isSessionReady,
    };
  },
  {
    persist: {
      pick: ['sessions', 'selectedSessionId', 'mainSession.id'],
      debug: true,
    },
  },
);

export default useSessionStore;
